import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Plus, PencilSimple, Trash, ListNumbers, 
  CalendarCheck, ArrowUp, ArrowDown, X,
  Image as ImageIcon
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";

export default function SeriesManager() {
  const { user } = useAuth();
  const [series, setSeries] = useState(null);
  const [err, setErr] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [organizingSeries, setOrganizingSeries] = useState(null);
  const [busy, setBusy] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    cover_image: null,
    is_published: true
  });

  const load = async () => {
    try {
      const { data } = await api.get("/series");
      setSeries(data);
    } catch (e) { setErr("Failed to load series"); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingSeries) {
        await api.put(`/series/${editingSeries.id}`, formData);
        toast.success("Series updated");
      } else {
        await api.post("/series", formData);
        toast.success("Series created");
      }
      setIsCreating(false);
      setEditingSeries(null);
      setFormData({ title: "", description: "", cover_image: null, is_published: true });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Operation failed");
    } finally { setBusy(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this series? Articles will be kept but unassigned.")) return;
    try {
      await api.delete(`/series/${id}`);
      toast.success("Series deleted");
      load();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const startEdit = (s) => {
    setEditingSeries(s);
    setFormData({
      title: s.title,
      description: s.description || "",
      cover_image: s.cover_image,
      is_published: !!s.is_published
    });
    setIsCreating(true);
  };

  if (organizingSeries) {
    return <SeriesOrganizer series={organizingSeries} onBack={() => { setOrganizingSeries(null); load(); }} />;
  }

  return (
    <div className="pb-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
        <div>
          <div className="eyebrow text-muted-foreground mb-2">Manage your story arcs</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-black tracking-tight">Series</h1>
        </div>
        {!isCreating && (
          <Button onClick={() => setIsCreating(true)} className="rounded-none bg-foreground text-background hover:bg-foreground/90">
            <Plus size={14} className="mr-1.5" /> New Series
          </Button>
        )}
      </div>

      {isCreating && (
        <div className="border border-border bg-card p-6 mb-8 animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl font-bold">{editingSeries ? "Edit Series" : "Create New Series"}</h2>
            <Button variant="ghost" size="icon" onClick={() => { setIsCreating(false); setEditingSeries(null); }}>
              <X size={20} />
            </Button>
          </div>
          <form onSubmit={handleSave} className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-8 space-y-6">
              <div>
                <Label className="eyebrow">Title</Label>
                <Input 
                  required
                  value={formData.title} 
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  className="rounded-none mt-1"
                  placeholder="The Chronicles of..."
                />
              </div>
              <div>
                <Label className="eyebrow">Description</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  className="rounded-none mt-1"
                  rows={4}
                  placeholder="What is this series about?"
                />
              </div>
              <div className="flex items-center gap-3">
                <Label className="eyebrow">Publicly Visible</Label>
                <Switch 
                  checked={formData.is_published} 
                  onCheckedChange={v => setFormData({...formData, is_published: v})}
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={busy} className="rounded-none bg-foreground text-background">
                  {busy ? "Saving..." : (editingSeries ? "Update Series" : "Create Series")}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setIsCreating(false); setEditingSeries(null); }} className="rounded-none">
                  Cancel
                </Button>
              </div>
            </div>
            <div className="col-span-12 md:col-span-4">
              <Label className="eyebrow block mb-2">Cover Image</Label>
              <ImageUploader 
                value={formData.cover_image} 
                onChange={v => setFormData({...formData, cover_image: v})} 
              />
            </div>
          </form>
        </div>
      )}

      {err ? <div className="text-sm text-destructive">{err}</div> : null}
      
      {series === null ? (
        <div className="text-muted-foreground">Loading...</div>
      ) : series.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <div className="font-serif text-2xl font-bold">No series yet</div>
          <p className="mt-2 text-sm text-muted-foreground">Group your articles into a collection with a specific order.</p>
          <Button onClick={() => setIsCreating(true)} className="mt-6 rounded-none bg-foreground text-background hover:bg-foreground/90">
            <Plus size={14} className="mr-1.5" /> Create your first series
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {series.map(s => (
            <div key={s.id} className="border border-border bg-card p-4 flex flex-col sm:flex-row gap-4 lift-card">
              <div className="w-full sm:w-32 h-32 bg-secondary flex-shrink-0">
                {s.cover_image ? (
                  <img src={fileUrl(s.cover_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-muted-foreground"><ImageIcon size={32} weight="thin" /></div>
                )}
              </div>
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-black uppercase tracking-widest ${s.is_published ? "text-emerald-600" : "text-amber-600"}`}>
                      {s.is_published ? "Published" : "Private"}
                    </span>
                    <span className="text-[10px] eyebrow text-muted-foreground">{s.articles_count} articles</span>
                  </div>
                  <h3 className="font-serif text-xl font-bold mt-1 line-clamp-1">{s.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 italic">{s.description || "No description."}</p>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="rounded-none flex-1" onClick={() => setOrganizingSeries(s)}>
                    <ListNumbers size={14} className="mr-1" /> Organize
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => startEdit(s)}>
                    <PencilSimple size={14} />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => handleDelete(s.id)}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SeriesOrganizer({ series, onBack }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    start_date: new Date().toISOString().split('T')[0],
    interval_days: 1
  });

  const loadArticles = async () => {
    try {
      const { data } = await api.get(`/series/${series.id}`);
      setArticles(data.articles || []);
    } catch (e) { toast.error("Failed to load articles"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadArticles(); }, [series.id]);

  const move = async (index, direction) => {
    const newArticles = [...articles];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= articles.length) return;
    
    const temp = newArticles[index];
    newArticles[index] = newArticles[targetIndex];
    newArticles[targetIndex] = temp;
    
    setArticles(newArticles);
    saveOrder(newArticles.map(a => a.id));
  };

  const saveOrder = async (ids) => {
    try {
      await api.post(`/series/${series.id}/reorder`, { article_ids: ids });
    } catch (e) { toast.error("Failed to save order"); }
  };

  const handleAutoSchedule = async () => {
    setBusy(true);
    try {
      await api.post(`/series/${series.id}/auto-schedule`, scheduleData);
      toast.success("Articles scheduled successfully");
      setScheduling(false);
      loadArticles();
    } catch (e) { toast.error("Failed to schedule"); }
    finally { setBusy(false); }
  };

  return (
    <div className="pb-10">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={onBack} className="rounded-none">
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
        <h1 className="font-serif text-3xl font-black">{series.title} — Playlist</h1>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-8">
          {loading ? (
            <div className="text-muted-foreground">Loading...</div>
          ) : articles.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center">
              <p className="text-muted-foreground mb-4">No articles assigned to this series yet.</p>
              <Button asChild variant="outline" className="rounded-none">
                <Link to="/dashboard">Go to Articles to assign</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {articles.map((a, i) => (
                <div key={a.id} className="flex items-center gap-4 border border-border bg-card p-3">
                  <div className="w-8 h-8 flex-shrink-0 bg-secondary text-muted-foreground grid place-items-center font-bold text-xs">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold truncate">{a.title}</h4>
                    <div className="flex items-center gap-2 text-[10px] eyebrow text-muted-foreground">
                      {a.publish_at ? (
                        <span className="flex items-center gap-1"><CalendarCheck size={12} /> {new Date(a.publish_at).toLocaleDateString()}</span>
                      ) : (
                        <span>Draft</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, -1)} disabled={i === 0}>
                      <ArrowUp size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, 1)} disabled={i === articles.length - 1}>
                      <ArrowDown size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <aside className="col-span-12 lg:col-span-4">
          <div className="border border-border bg-card p-6">
            <h3 className="eyebrow text-muted-foreground mb-4">Playlist Options</h3>
            {!scheduling ? (
              <Button className="w-full rounded-none" variant="outline" onClick={() => setScheduling(true)}>
                <CalendarCheck size={16} className="mr-2" /> Auto-Schedule (Daily)
              </Button>
            ) : (
              <div className="space-y-4 animate-in fade-in">
                <div>
                  <Label className="eyebrow text-[10px]">Start Date</Label>
                  <Input 
                    type="date" 
                    value={scheduleData.start_date} 
                    onChange={e => setScheduleData({...scheduleData, start_date: e.target.value})}
                    className="rounded-none h-8 text-xs mt-1"
                  />
                </div>
                <div>
                  <Label className="eyebrow text-[10px]">Interval (Days)</Label>
                  <Input 
                    type="number" 
                    value={scheduleData.interval_days} 
                    onChange={e => setScheduleData({...scheduleData, interval_days: e.target.value})}
                    className="rounded-none h-8 text-xs mt-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAutoSchedule} disabled={busy} className="rounded-none flex-1">
                    {busy ? "Scheduling..." : "Apply"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setScheduling(false)} className="rounded-none">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-4 italic">
              * Reordering articles will update their sequence in the public "playlist" view.
              <br/><br/>
              * Auto-scheduling will set sequential publication dates for all articles in the series.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ArrowLeft({ size, className }) {
  return <svg width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
